import {
  EyeIcon,
  GlobeAmericasIcon,
  HeartIcon,
  LightBulbIcon,
  ScaleIcon,
  ShieldCheckIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

import React from 'react';

const About: React.FC = () => {
  const values = [
    {
      name: 'Unity',
      description: 'We believe in the strength of collective identity and shared purpose.',
      icon: UsersIcon,
      color: 'text-purple-600'
    },
    {
      name: 'Empowerment',
      description: 'We prioritize access, opportunity, and upliftment for all.',
      icon: LightBulbIcon,
      color: 'text-yellow-600'
    },
    {
      name: 'Equity',
      description: 'We are committed to dismantling systemic barriers and promoting fairness.',
      icon: ScaleIcon,
      color: 'text-blue-600'
    },
    {
      name: 'Excellence',
      description: 'We celebrate and promote achievement in all its forms.',
      icon: ShieldCheckIcon,
      color: 'text-green-600'
    },
    {
      name: 'Cultural Pride',
      description: 'We honour and affirm the richness of Black heritage across the diaspora.',
      icon: HeartIcon,
      color: 'text-red-600'
    },
    {
      name: 'Integrity',
      description: 'Operating with transparency, honesty, and ethical principles in all we do.',
      icon: GlobeAmericasIcon,
      color: 'text-indigo-600'
    }
  ];

  const focusAreas = [
    {
      title: 'Professional & Business Development',
      description: 'Foster professional growth and entrepreneurship through training, mentorship, and business support programs that advance economic empowerment.',
      initiatives: [
        'Black Business Networking Series',
        'Entrepreneurship support programs',
        'Professional mentorship initiatives',
        'Skills development workshops'
      ]
    },
    {
      title: 'Civic & Political Engagement',
      description: 'Promote active participation in democratic processes and community decision-making to strengthen Black voices in Canadian society.',
      initiatives: [
        'Civic Engagement & Voter Education Campaigns',
        'Community leadership training',
        'Policy advocacy initiatives',
        'National Advocacy and Policy Forums'
      ]
    },
    {
      title: 'Youth & Leadership Development',
      description: 'Support the next generation with mentorship, scholarships, and leadership opportunities that build capacity and confidence.',
      initiatives: [
        'Youth Mentorship & Leadership Academy',
        'Scholarship programs',
        'Emerging leaders development',
        'Educational resource development'
      ]
    },
    {
      title: 'Cultural Celebration & Arts',
      description: 'Amplify Black excellence in the arts, trades, and entrepreneurship through showcases, grants, and community celebrations.',
      initiatives: [
        'Cultural Festivals and Celebrations',
        'Arts and media campaigns',
        'Heritage preservation projects',
        'Cultural education programs'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 text-white bg-gradient-to-r from-primary-800 to-primary-900">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="mb-6 text-4xl font-bold md:text-5xl font-heading">
              About PBCN
            </h1>
            <p className="max-w-3xl mx-auto text-xl text-gray-200 md:text-2xl">
              A national, Black-led nonprofit organization uniting people of Black descent across Canada, 
              committed to creating spaces for empowerment, advocacy, and community-driven progress.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl font-heading">
                Our Story
              </h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  The Professional Black Canadians Network (PBCN) is more than just an organization—we are a movement. 
                  Founded as a national, Black-led nonprofit, we represent a thriving community of professionals, 
                  entrepreneurs, and leaders committed to advancing Black excellence across Canada.
                </p>
                <p>
                  Our network spans coast to coast, bringing together diverse voices and experiences united by a common 
                  vision: creating pathways to success and ensuring that Black excellence is recognized, celebrated, 
                  and supported at every level of Canadian society.
                </p>
                <p>
                  Through strategic partnerships, innovative programs, and unwavering advocacy, we work to dismantle 
                  barriers and create opportunities that enable Black Canadians to thrive in their chosen fields and 
                  contribute meaningfully to their communities.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="/assets/cultural-arts.jpg"
                alt="Community meeting"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute inset-0 rounded-lg bg-gradient-to-t from-primary-900/20 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="p-8 bg-white shadow-lg rounded-xl">
              <div className="flex items-center mb-6">
                <EyeIcon className="w-8 h-8 mr-3 text-primary-600" />
                <h3 className="text-2xl font-bold text-gray-900 font-heading">Our Vision</h3>
              </div>
              <p className="text-lg leading-relaxed text-gray-700">
                A thriving and united national network that advances Black excellence, equity, 
                and leadership in all sectors of Canadian society. We envision communities where 
                Black Canadians have equal access to opportunities and their contributions are 
                fully recognized and valued.
              </p>
            </div>
            <div className="p-8 bg-white shadow-lg rounded-xl">
              <div className="flex items-center mb-6">
                <HeartIcon className="w-8 h-8 mr-3 text-primary-600" />
                <h3 className="text-2xl font-bold text-gray-900 font-heading">Our Mission</h3>
              </div>
              <p className="text-lg leading-relaxed text-gray-700">
                To empower and connect Black communities through economic advancement, education, 
                advocacy, and cultural celebration. We work to foster professional and business 
                development, promote civic engagement, and support youth and emerging leaders across Canada.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl font-heading">
              Our Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide our work and decision-making
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {values.map((value) => (
              <div
                key={value.name}
                className="p-6 transition-shadow duration-300 bg-white border border-gray-100 shadow-lg rounded-xl hover:shadow-xl"
              >
                <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-lg bg-gray-50">
                  <value.icon className={`h-6 w-6 ${value.color}`} />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  {value.name}
                </h3>
                <p className="leading-relaxed text-gray-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl font-heading">
              Our Focus Areas
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive approach to creating positive change
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {focusAreas.map((area) => (
              <div
                key={area.title}
                className="p-8 transition-shadow duration-300 bg-white shadow-lg rounded-xl hover:shadow-xl"
              >
                <h3 className="mb-4 text-2xl font-semibold text-gray-900">
                  {area.title}
                </h3>
                <p className="mb-6 leading-relaxed text-gray-700">
                  {area.description}
                </p>
                <div>
                  <h4 className="mb-3 text-lg font-semibold text-gray-900">Key Initiatives:</h4>
                  <ul className="space-y-2">
                    {area.initiatives.map((initiative) => (
                      <li key={initiative} className="flex items-center text-gray-600">
                        <div className="w-2 h-2 mr-3 rounded-full bg-primary-600"></div>
                        {initiative}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 text-white bg-primary-800">
        <div className="max-w-4xl px-4 mx-auto text-center sm:px-6 lg:px-8">
          <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
            Join Our Community
          </h2>
          <p className="mb-8 text-xl leading-relaxed text-gray-200">
            Be part of the movement for equality and justice. Together, we can build a stronger, 
            more inclusive Canada for all Black Canadians.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 font-semibold text-white transition-colors duration-200 rounded-lg bg-secondary-600 hover:bg-secondary-700"
            >
              Get Involved
            </a>
            <a
              href="/donate"
              className="inline-flex items-center px-8 py-4 font-semibold text-white transition-colors duration-200 border-2 border-white rounded-lg hover:bg-white hover:text-primary-800"
            >
              Support Our Work
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
